import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { browserTimezone, formatDateTime } from '../utils/datetime';

// Prefer the browser's own IANA catalog (~500 zones on modern engines) so we
// stay in sync with whatever Intl actually supports; fall back to a tiny list
// on old runtimes so the picker is never empty.
function supportedTimezones(): string[] {
  try {
    // Intl.supportedValuesOf is standard as of ES2022 but TS lib defs vary,
    // so cast through unknown to avoid pinning the whole project's TS target.
    const fn = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf;
    if (typeof fn === 'function') return fn.call(Intl, 'timeZone');
  } catch {
    /* fall through */
  }
  return ['UTC', 'America/New_York', 'America/Los_Angeles', 'Europe/London', 'Europe/Berlin', 'Asia/Kolkata', 'Asia/Tokyo', 'Australia/Sydney'];
}

interface Props {
  open: boolean;
  onClose: () => void;
  // When true, the user is being nudged post-login (can't dismiss without picking).
  // When false, they're editing from the profile page (a Cancel button is offered).
  required?: boolean;
}

const TimezoneSetupModal: React.FC<Props> = ({ open, onClose, required = false }) => {
  const { user, refreshUser } = useAuth();
  const detected = useMemo(browserTimezone, []);
  const zones = useMemo(supportedTimezones, []);
  const [value, setValue] = useState<string>(user?.timezone || detected);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const now = useMemo(() => new Date().toISOString(), []);

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    try {
      await apiService.updateOwnPreferences({ timezone: value });
      await refreshUser();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not save timezone. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      // When required, swallow escape / backdrop close paths so a first-login
      // user can't Esc past the setup step. MUI v9 no longer takes
      // `disableEscapeKeyDown` directly, so we filter the reason ourselves.
      onClose={(_event, reason) => {
        if (required && (reason === 'escapeKeyDown' || reason === 'backdropClick')) return;
        onClose();
      }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <PublicOutlinedIcon fontSize="small" />
        <Box>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }}>
            Confirm your timezone
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.25 }}>
            Timestamps across the dashboard will be shown in this zone.
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Autocomplete
          value={value}
          onChange={(_, v) => v && setValue(v)}
          options={zones}
          disableClearable
          autoHighlight
          openOnFocus
          renderInput={(params) => (
            <TextField
              {...params}
              label="Timezone"
              helperText={value === detected ? 'Detected from your browser' : `Detected: ${detected}`}
              size="small"
              margin="dense"
            />
          )}
        />

        <Box sx={{
          mt: 2, p: 1.25,
          borderRadius: 1,
          bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
        }}>
          <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', letterSpacing: '0.05em', textTransform: 'uppercase', fontWeight: 600 }}>
            Preview
          </Typography>
          <Typography sx={{ fontSize: '0.9rem', mt: 0.5, fontFamily: 'monospace' }}>
            {formatDateTime(now, value)}
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mt: 2, fontSize: '0.8rem' }}>{error}</Alert>}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {!required && (
          <Button onClick={onClose} disabled={saving} size="small">
            Cancel
          </Button>
        )}
        <Button
          onClick={handleSave}
          disabled={saving || !value}
          variant="contained"
          size="small"
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TimezoneSetupModal;
